import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type ReactNode,
} from "react";
import { Drawer } from "../../../shared/components/Drawer";
import { BP, useMediaQuery } from "../../../shared/hooks/useMediaQuery";
import {
  DataSurface,
  StatusPill,
  AlertIcon,
  CheckIcon,
  ChevronIcon,
  ChildrenIcon,
  InfoIcon,
  PlayIcon,
  PrivacyIcon,
  ReportsIcon,
  SchoolIcon,
  TrendUpIcon,
  VoiceIcon,
  /* Initials instead of 👩‍🏫, and the real sign-out glyph instead of "↪". */
  Avatar,
  SignOutIcon,
  type StatTone,
} from "../../../shared/components";
import { useResource } from "../../../shared/hooks/useResource";
/* §10 — the one push channel: a school declaring a closure reaches an open
   staffroom laptop before somebody acts on the old answer. See `StaffNotices`. */
import { useStaffEvents } from "../../../shared/hooks/useStaffEvents";
import { getClasses, type ClassSummary } from "../../../shared/api/classes";
import {
  getStaffIdentity,
  type StaffIdentity,
} from "../../../shared/api/auth";
import type { Role } from "../../../shared/types/prisma-enums";
import { coverageCsv, type StaffAuth } from "../../../shared/api/teaching";
import { getClassPlayCode, groupCode } from "../../../shared/api/play";
import { GAMES } from "../../../shared/curriculum/games";
import {
  ClassOverviewScreen,
  LaunchConfirm,
  LeaderboardScreen,
  /* ⚠️ NO `ModeSelect` — D2 PICKS A CHILD AND LANDS ON D3, and the machine has
     no D2 → mode edge at all (see the `PICK_CHILD` dispatch below). The screen
     is still exported from `index.ts` for the tier that does use it. */
  ReportsScreen,
  RosterScreen,
} from "./TeacherScreens";
import { PlaySetupScreen } from "./PlaySetupScreen";
/* Teacher Play Mode — the four writes that turn a tap into a child's round. */
import { launchPlayAs } from "./playLaunch";
import { StudentPage } from "./StudentPage";
import { TEACHER_COPY, describeError, type Lang } from "./copy";
import { initialTeacherFlow, teacherFlow } from "./teacherFlow";
import {
  chooseClass,
  classLabel,
  findClass,
  groupByGrade,
  mayTakeRegister,
  resolveSelected,
  signedInName,
} from "./classChoice";
import { NavItem } from "./TeacherUI";
import { StudentsScreen } from "./StudentsScreen";
import { AttendanceScreen } from "./AttendanceScreen";
import { MessagesScreen } from "./MessagesScreen";
import { ClassNotices } from "./ClassNotices";

/**
 * The teacher tier, assembled — D1 through D7.
 *
 * 🔴 WHY THIS FILE EXISTS: FIVE OF SEVEN TEACHER SCREENS WERE MOUNTED NOWHERE,
 * AND THE STATE MACHINE THAT CONNECTS THEM WAS IMPORTED ONLY BY ITS OWN TEST.
 *
 * `AppRouter` rendered `ClassOverviewScreen` and `StudentDetail` behind a
 * hand-rolled two-screen `useState`, and left `ModeSelect`, `RosterScreen`,
 * `LaunchConfirm`, `LeaderboardScreen` and `ReportsScreen` unreachable —
 * written, tested, bilingual, calling real endpoints, in the production bundle
 * only if the tree-shaker happened to keep them. That is OD-89's failure for the
 * third time, and `teacherFlow.ts` is the answer that was already written:
 * *"navigation that lives in component state cannot be tested, and the
 * transition that matters here — you cannot reach a game without passing the
 * confirm screen — is a claim about the graph, not about a button."*
 *
 * 🔴 AND IT FIXES THE CRASH THE TIER SHIPPED WITH. The router held
 * `classId = ''`, so every screen issued `GET /classes//overview` — a path Nest
 * has no route for, answering 404, which the surface reported honestly as
 * *"Couldn't load this."* `GET /classes` was already built and scoped; nothing
 * called it. `classChoice.ts` owns the decision of which class opens.
 *
 * ⚠️ FLAT, NOT A SIDEBAR. `information-architecture.md` §2: the teacher tier is
 * *"a two-mode fork, then a flat surface — one slot, no hunting"*. Leaderboard
 * and Reports hang off the dashboard rather than a persistent menu, because a
 * menu costs taps inside the thirty-minute slot, *"which is the moment adoption
 * is won or lost"*.
 */

export interface TeacherAppProps {
  lang?: Lang;
  auth?: StaffAuth;
  /** Shown on D1. The account's own name — never guessed from the class. */
  teacherName?: string;
  /**
   * 🔴 WHO IS SIGNED IN — AND IT IS PRESENTATION, NEVER AUTHORISATION.
   *
   * `routes.ts` lands both a TEACHER and a COORDINATOR on this tier, and S6
   * makes a coordinator a multi-class READ role: the server refuses them every
   * homework write (`HomeworkService.writableClass` enumerates all five roles
   * and their reasons). This decides whether they are SHOWN a form, which is
   * D4's rule — *"a control that cannot work is worse than no control"*. The
   * refusal itself stays where it has always been.
   */
  role?: Role | null;
  /** Rendered in the sidebar, because this tier owns its own chrome. */
  onSignOut?: () => void;
}

export function TeacherApp({
  lang = "en",
  auth,
  teacherName,
  role: _role,
  onSignOut,
}: TeacherAppProps) {
  const t = TEACHER_COPY[lang];

  const classes = useResource<ClassSummary[]>(
    (signal) => getClasses({ ...auth, signal }),
    ["teacher-classes"],
    /*
     * ⚠️ EMPTY MEANS "NO CLASS ASSIGNED", WHICH IS A REAL AND FIXABLE STATE
     * rather than a failure — and the fix belongs to an admin, not to the
     * teacher looking at it. The empty copy says exactly that.
     */
    {
      isEmpty: (rows) => rows.filter((c) => c.archivedAt === null).length === 0,
    },
  );

  /*
   * 🔴 THE PICK IS NOT PRE-FILLED WHEN THERE IS A CHOICE. `chooseClass` states
   * the rule; this state is only ever set by the teacher touching the picker.
   */
  const [picked, setPicked] = useState<string | null>(null);

  /*
   * ✅ WHO IS SIGNED IN, ASKED RATHER THAN INFERRED.
   *
   * 🔴 THIS SCREEN USED TO GREET PEOPLE BY THE CLASS TEACHER'S NAME. `teacherName`
   * is optional and the router never passed it, so the fallback below it —
   * `openClass?.teacher?.name` — was what actually rendered. For as long as a
   * teacher could only open a class they RAN, that name was always their own and
   * the bug was invisible. E4's subject teachers open classes they do not run,
   * and the greeting started addressing them by a colleague's name.
   *
   * ⚠️ ITS FAILURE IS NOT THIS SCREEN'S FAILURE. A name is decoration on a
   * dashboard whose subject is a class; if the call fails the greeting drops the
   * name (`greeting("")` is "Hello") and nothing else changes. It is deliberately
   * NOT wired into `classes.state`, which is what decides whether there is a
   * class to show at all.
   */
  const identity = useResource<StaffIdentity>(
    (signal) => getStaffIdentity({ ...auth, signal }),
    ["staff-identity"],
  );

  /* The account's own name, or nothing. Never another person's — the branches
     are in `classChoice.ts`, because this file is one no test can reach. */
  const greetBy = signedInName(identity.data, teacherName);

  const rows = useMemo(() => classes.data ?? [], [classes.data]);
  const choice = useMemo(() => chooseClass(rows), [rows]);
  /*
   * Re-validated on every render against what the server last returned — a class
   * archived mid-morning must not leave the tier rendering 403s with no
   * explanation. See `resolveSelected`.
   */
  const classId = resolveSelected(rows, picked);
  const openClass = findClass(rows, classId);

  /*
   * 🔴 THE FORK IS BEFORE THE DATA SURFACE, NOT INSIDE IT, AND THAT IS WHAT
   * GIVES THE FOUR STATES A PAGE.
   *
   * `DataSurface` used to wrap everything, so loading, error and empty all
   * rendered as bare text in the top-left corner of an otherwise blank viewport
   * — no wordmark, no chrome, and (see `ClassGate`) no way to sign out. They
   * could not be framed from outside because the same wrapper also contained
   * `TeacherFlowScreens`, which draws its own full-page sidebar layout and must
   * not be framed twice.
   *
   * Splitting on `classId` separates the two shapes honestly: a class is open,
   * or the teacher is still at the gate. Every gate state now gets the gate.
   */
  if (classId !== null) {
    return (
      <TeacherFlowScreens
        key={classId}
        classId={classId}
        classes={rows}
        className={openClass === null ? "" : classLabel(openClass)}
        /* 🔴 NEVER `openClass?.teacher?.name`. That is who RUNS this class, and
           printing it as "Hello, X" tells a subject teacher they are somebody
           else. An empty string is the honest answer, and the copy has a
           sentence for it. */
        teacherName={greetBy}
        canChangeClass={choice.kind === "MANY"}
        /*
         * 🔴 E4 — MAY THIS PERSON TAKE THIS CLASS'S REGISTER, OR ONLY READ IT?
         * A subject teacher reaches the class through `ClassSubject`; the one
         * write the server refuses them is attendance, because taking a register
         * messages families. `mayTakeRegister` holds the branch and the reason.
         */
        canTakeRegister={mayTakeRegister(identity.data, openClass)}
        registerHeldBy={openClass?.teacher?.name ?? null}
        onPickClass={setPicked}
        onSignOut={onSignOut}
        lang={lang}
        auth={auth}
        onDataChanged={classes.retry}
      />
    );
  }

  return (
    <ClassGate lang={lang} onSignOut={onSignOut}>
      <DataSurface
        state={classes.state}
        skeletonRows={3}
        /*
         * 🔴 THE EMPTY STATE NAMES WHO FIXES IT — AND UNTIL NOW IT COULD NOT.
         *
         * The sentence that does the naming (*"Ask your school admin to assign
         * you to a class"*) sat in `DataSurface`'s CHILDREN, under a comment
         * explaining why it mattered. `DataSurface` returns its empty state
         * early and never renders children, so a teacher with no class saw only
         * *"You are not assigned to a class yet"* — the problem with no owner,
         * which is precisely what the comment said must not happen. It is the
         * `emptyMessage` now, where the component will actually draw it.
         */
        /*
         * ⚠️ LEFT-ALIGNED, THOUGH THE PICKER'S HEADING IS CENTRED. `DataSurface`
         * lays its empty state out as `flex flex-col items-start` — one sentence
         * and one action — and centring text inside a shrink-to-fit paragraph
         * would centre it against its own width rather than the page's, which
         * looks like a mistake rather than a choice. The gate around it does the
         * framing; this stays in the shape the component draws.
         */
        emptyMessage={
          <span className="block">
            <span className="block text-dash-lg font-extrabold text-ink">
              {t.noClass}
            </span>
            <span className="mt-2 block max-w-[46ch] text-dash-sm text-muted">
              {t.noClassHint}
            </span>
          </span>
        }
        errorMessage={describeError(classes.error, lang)}
        errorLastWorked={
          classes.lastLoadedAt
            ? t.lastWorked(classes.lastLoadedAt.toLocaleTimeString())
            : t.neverWorked
        }
        errorRetryLabel={t.retry}
        onRetry={classes.retry}
      >
        <ClassPicker choice={choice} lang={lang} onPick={setPicked} />
      </DataSurface>
    </ClassGate>
  );
}

/**
 * 🔴 THE COLOUR ON A CLASS CARD IS IDENTITY, NOT DECORATION AND NOT STATUS.
 *
 * Four tones from the pond palette, assigned by position across the whole list,
 * so a teacher's second class is the same colour every morning — the rule
 * `StatCard` states. A class has no status for a colour to encode, and §12
 * requires status to be icon + colour + text, always all three.
 */
const CLASS_TONES: StatTone[] = ["primary", "teal", "leaf", "indigo"];

/**
 * The frame both pre-class states share — the picker, and "you have no class".
 *
 * 🔴 IT EXISTS BECAUSE THIS TIER HAD NO CHROME AT ALL UNTIL A CLASS WAS CHOSEN,
 * AND ONE CONSEQUENCE OF THAT WAS A BUG RATHER THAN A LOOK.
 *
 * `onSignOut` reached `TeacherFlowScreens` and nothing else, so the sidebar's
 * sign-out only existed once a class was open. A teacher who signed in and
 * stopped here — or who has no class assigned at all — had **no way out of the
 * session**, on the shared classroom laptop this product is designed around.
 * `AppRouter` states the rule for every other tier: *"a sign-out an adult has to
 * hunt for is a session left open for the next class."* Here there was nothing
 * to hunt for.
 *
 * ⚠️ IT IS NOT `DashboardShell`, AND NOT `AuthShell`. The first draws a sidebar
 * of routes, and this screen's whole point is that no class is open yet, so
 * there is nothing to navigate. The second wraps its children in a bordered card
 * (A2's sign-in box), which would put a grid of class cards inside a card. What
 * is shared with `AuthShell` is the part that matters — the wordmark's own block
 * sequence as a rule across the top, which is the one element the marketing
 * page, A1 and every auth screen have in common, so a teacher can see this is
 * still the same product.
 */
function ClassGate({
  lang,
  onSignOut,
  children,
}: {
  lang: Lang;
  onSignOut?: () => void;
  children: ReactNode;
}) {
  const t = TEACHER_COPY[lang];

  return (
    <div className="flex min-h-dvh flex-col bg-canvas relative overflow-hidden">
      {/* Multi-color 3px top accent bar */}
      <div aria-hidden="true" className="flex h-[3px] w-full shrink-0">
        {["bg-teal", "bg-sunshine", "bg-leaf", "bg-primary", "bg-sunshine"].map(
          (bg, i) => (
            <span key={`${bg}-${String(i)}`} className={`flex-1 ${bg}`} />
          ),
        )}
      </div>

      <header className="flex shrink-0 flex-wrap items-center justify-between gap-4 px-6 py-5 lg:px-10 z-10">
        <div className="flex items-center gap-3">
          <img
            src="/kalqy-wordmark.png"
            alt="Kalqy"
            width={2000}
            height={585}
            className="h-8 w-auto"
          />
          <span className="border-l border-line-strong pl-3 font-dash text-dash-xs font-bold tracking-[0.12em] text-muted uppercase">
            {t.navClasses}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {onSignOut && (
            <button
              type="button"
              onClick={onSignOut}
              className="rounded-full border border-pink-200 bg-surface px-4 py-2 font-dash text-dash-xs font-bold text-primary transition-colors duration-150 ease-dash hover:bg-primary-tint flex items-center gap-1.5 shadow-sm"
            >
              <SignOutIcon size={15} /> {t.signOut}
            </button>
          )}
        </div>
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-6 pt-2 pb-24 lg:px-10 z-10">
        <div className="w-full max-w-[52.5rem]">{children}</div>
      </main>

      {/* Decorative Bottom Garden Backdrop */}
      <div className="pointer-events-none select-none absolute bottom-0 inset-x-0 z-0">
        {/*
          ⚠️ RASTER, WHERE EVERY OTHER DECORATION IN THIS APP IS INLINE SVG. The
          meadow is a soft-gradient illustration that hand-written paths do not
          reproduce, so it ships as the one decorative PNG in `public/`. It is
          `aria-hidden` with an empty `alt` because it says nothing a teacher
          needs.

          ⚠️ `h-auto`, AND NO FIXED BAND HEIGHT. The art was cropped to its own
          content box — the source carried 262px of dead transparency on top —
          so letting it keep its aspect ratio is what stops it being squashed on
          a narrow viewport or having its leaf tops cropped on a wide one. The
          strip's height therefore tracks the viewport width, the way the
          marketing footer's garden does.
        */}
        <img
          src="/teacher-meadow.png"
          alt=""
          aria-hidden="true"
          width={2172}
          height={462}
          className="block h-auto w-full"
        />
      </div>
    </div>
  );
}

function ClassCard({
  klass,
  tone,
  childrenLabel,
  onPick,
}: {
  klass: ClassSummary;
  tone: StatTone;
  childrenLabel: string;
  onPick: () => void;
}) {
  const toneStyles: Record<
    StatTone,
    {
      borderLeft: string;
      iconBg: string;
      iconColor: string;
      arrowBg: string;
      arrowColor: string;
    }
  > = {
    primary: {
      borderLeft: "border-l-primary",
      iconBg: "bg-primary-tint",
      iconColor: "text-primary",
      arrowBg: "bg-primary-tint",
      arrowColor: "text-primary",
    },
    teal: {
      borderLeft: "border-l-[#0284c7]",
      iconBg: "bg-[#e0f2fe]",
      iconColor: "text-[#0284c7]",
      arrowBg: "bg-[#e0f2fe]",
      arrowColor: "text-[#0284c7]",
    },
    leaf: {
      borderLeft: "border-l-[#16a34a]",
      iconBg: "bg-[#dcfce7]",
      iconColor: "text-[#16a34a]",
      arrowBg: "bg-[#dcfce7]",
      arrowColor: "text-[#16a34a]",
    },
    sunshine: {
      borderLeft: "border-l-[#8b5cf6]",
      iconBg: "bg-[#f3e8ff]",
      iconColor: "text-[#8b5cf6]",
      arrowBg: "bg-[#f3e8ff]",
      arrowColor: "text-[#8b5cf6]",
    },
    coral: {
      borderLeft: "border-l-[#f97316]",
      iconBg: "bg-[#ffedd5]",
      iconColor: "text-[#f97316]",
      arrowBg: "bg-[#ffedd5]",
      arrowColor: "text-[#f97316]",
    },
    indigo: {
      borderLeft: "border-l-[#6366f1]",
      iconBg: "bg-[#e0e7ff]",
      iconColor: "text-[#6366f1]",
      arrowBg: "bg-[#e0e7ff]",
      arrowColor: "text-[#6366f1]",
    },
  };

  const style = toneStyles[tone] ?? toneStyles.primary;

  return (
    <button
      type="button"
      onClick={onPick}
      className={`group flex w-full items-center justify-between gap-4 rounded-2xl border border-line bg-surface p-5 text-left shadow-card transition-all duration-200 ease-dash hover:-translate-y-1 hover:shadow-md cursor-pointer border-l-[5px] ${style.borderLeft}`}
    >
      <div className="flex items-center gap-4 min-w-0">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${style.iconBg} ${style.iconColor}`}
        >
          <SchoolIcon size={24} strokeWidth={2.2} />
        </div>

        <div className="min-w-0">
          <span className="block truncate font-dash text-dash-lg font-extrabold text-ink">
            {klass.name}
          </span>
          <span className="mt-0.5 flex flex-wrap items-center gap-1.5 font-dash text-dash-xs font-bold text-muted">
            <ChildrenIcon size={14} strokeWidth={2.4} />
            <span className="tabular">{klass.childCount}</span>
            {childrenLabel}
            {/*
              🔴 THE SIGN-IN CODE, ON THE CARD A TEACHER ALREADY OPENS EVERY
              MORNING. It lived only on the play-setup screen, so the answer to
              "what do they type first" was two navigations away from the class
              itself. `select-all` because it is copied onto a whiteboard.
            */}
            {klass.code !== null && (
              <>
                <span aria-hidden="true">·</span>
                <span className="tabular select-all tracking-wider text-ink">
                  {klass.code}
                </span>
              </>
            )}
          </span>
        </div>
      </div>

      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${style.arrowBg} ${style.arrowColor} transition-transform group-hover:translate-x-1`}
      >
        <ChevronIcon size={18} />
      </div>
    </button>
  );
}

function ClassPicker({
  choice,
  lang,
  onPick,
}: {
  choice: ReturnType<typeof chooseClass>;
  lang: Lang;
  onPick: (classId: string) => void;
}) {
  const t = TEACHER_COPY[lang];
  const groups = useMemo(
    () => (choice.kind === "MANY" ? groupByGrade(choice.options) : []),
    [choice],
  );

  if (choice.kind !== "MANY") return null;

  const grouped = groups.length > 1;
  let index = -1;

  return (
    <>
      <div className="mb-10 text-center flex flex-col items-center">
        {/* Top 3D Schoolhouse Badge */}
        <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-[#fef3c7] p-2 border-4 border-white shadow-card">
          <svg className="w-12 h-12" viewBox="0 0 48 48" fill="none">
            <path d="M24 6 L6 18 V42 H42 V18 Z" fill="#38bdf8" />
            <path d="M24 6 L6 18 H42 Z" fill="#0284c7" />
            <rect x="18" y="26" width="12" height="16" rx="2" fill="#f59e0b" />
            <circle cx="24" cy="18" r="4" fill="#fef08a" />
            <path
              d="M24 2 V6"
              stroke="#ef4444"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <polygon points="24,2 30,4 24,6" fill="#ef4444" />
          </svg>
        </div>

        <h1 className="font-dash text-3xl font-extrabold text-[#1e293b]">
          {t.pickClass}
        </h1>
        <p className="mt-1.5 font-dash text-dash-sm font-medium text-muted max-w-[50ch]">
          {t.pickClassHint}
        </p>

        {/* Pink underline accent bar */}
        <div className="mt-3 h-1 w-12 rounded-full bg-primary" />
      </div>

      <div className="flex flex-col gap-8">
        {groups.map((group) => (
          <section key={group.grade ?? "—"}>
            {grouped && (
              <h2 className="mb-3 font-dash text-dash-xs font-bold tracking-wider text-muted uppercase">
                {group.grade ?? t.pickClass}
              </h2>
            )}
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {group.classes.map((c) => {
                index += 1;
                return (
                  <li key={c.id}>
                    <ClassCard
                      klass={c}
                      tone={CLASS_TONES[index % CLASS_TONES.length]}
                      childrenLabel={t.children.toLowerCase()}
                      onPick={() => {
                        onPick(c.id);
                      }}
                    />
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </>
  );
}

/**
 * D1–D7, driven by the machine rather than by ad-hoc state.
 *
 * 🔴 THE REDUCER IS THE NAVIGATION. Every transition below is `teacherFlow`'s,
 * which is where the one guarantee that matters lives: **there is no path from
 * D2 to a game that skips D3.** A component holding its own `screen` string
 * could grow one in a hurry on a Friday, and the cost is an entire session
 * attributed to the wrong child.
 */
function TeacherFlowScreens({
  classId,
  classes,
  className,
  teacherName,
  canChangeClass,
  canTakeRegister,
  registerHeldBy,
  onPickClass,
  onSignOut,
  lang,
  auth,
  onDataChanged,
}: {
  classId: string;
  /** Every class this teacher may move a child into. */
  classes: readonly ClassSummary[];
  className: string;
  teacherName: string;
  canChangeClass: boolean;
  /** E4 — false for a subject teacher on a class they do not run. */
  canTakeRegister: boolean;
  /** The class teacher of record, for the sentence that names them. */
  registerHeldBy: string | null;
  /**
   * Switch class straight from the sidebar, without going back to the picker.
   *
   * ⚠️ THERE IS DELIBERATELY NO `onChangeClass` ("back to the picker"). The
   * sidebar switcher below lists every class this teacher has, grouped by grade
   * — the same two levels the picker draws — so a route back to the picker
   * offers nothing the sidebar does not, and a nav item that lands somewhere
   * already reachable is one more thing to tap past mid-lesson.
   */
  onPickClass: (classId: string | null) => void;
  onSignOut?: () => void;
  lang: Lang;
  auth?: StaffAuth;
  /** A write landed on D5 — the class-level counts are now stale. */
  onDataChanged: () => void;
}) {
  const t = TEACHER_COPY[lang];
  const [flow, dispatch] = useReducer(teacherFlow, classId, initialTeacherFlow);

  /*
   * 🔴 THE CHILD'S NAME TRAVELS WITH THE ID, AND IT USED NOT TO.
   *
   * `AppRouter` opened `StudentDetail` with `childName: ''` and a comment
   * admitting it: *"the name is not on the callback, so the detail screen is
   * opened without one rather than with a guess."* Correct given the callback,
   * and the callback was the thing to fix — both screens that open a child
   * already have the name in the payload the teacher is looking at.
   */
  /*
   * ⚠️ THE ROSTER FETCH THAT USED TO LIVE HERE IS GONE. `StudentPage` reads the
   * roster itself, because it needs the parent linkage as well as the name —
   * and two components fetching the same list for one screen is the second
   * request nobody notices until a class of forty opens it.
   */
  const [, setOpenChild] = useState<{ id: string; name: string } | null>(null);

  /*
   * 🔴 TEACHER PLAY MODE — the one piece of state D3's launch needs, and the
   * only thing that can still go wrong once the button works.
   *
   * ⚠️ IT IS AN ERROR SLOT AND NOT A "LAUNCHING…" FLAG. The success path leaves
   * this component entirely — the hash changes to the child's app inside the
   * same tick the mint resolves — so a spinner would be a state nobody is on
   * screen long enough to see. A refusal is the one outcome that comes back.
   */
  const [launchError, setLaunchError] = useState<string | null>(null);

  const launch = useCallback(
    async (playerId: string, gameId: string) => {
      setLaunchError(null);
      try {
        await launchPlayAs({
          playerId,
          /*
           * ⚠️ NARROWED AGAINST THE CURRICULUM RATHER THAN CAST. `LaunchConfirm`
           * hands back whichever id its own picker was given, and that picker is
           * built from `GAMES` — but the prop is a bare string, so this is where
           * a game id that is not a game becomes `null` (the child chooses on
           * their own home screen) instead of a screen that renders nothing.
           */
          gameId: GAMES.find((g) => g.id === gameId)?.id ?? null,
          auth: auth ?? {},
          /* Back to this tier when the tablet comes back — the teacher's own
             session never ended, so the door picker would be a false alarm. */
          returnTo: "teacher",
        });
      } catch (err) {
        setLaunchError(describeError(err instanceof Error ? err : null, lang));
      }
    },
    [auth, lang],
  );

  const exportCoverage = useCallback(async () => {
    /*
     * ✅ IT DOWNLOADS THE FILE NOW, AND WHAT USED TO STAND HERE IS WHY IT DID
     * NOT. This resolved `coverageCsvPath(classId)` and threw the string away —
     * the note called that *"deliberately not offered as a working download"*,
     * because a plain link carries cookies and this API uses bearer tokens, so
     * an `<a href>` would have saved a 401 page named `ncf-coverage.csv`.
     *
     * The reasoning was right and the conclusion had expired. `client.ts` gained
     * `expect: 'text'` for X9's audit export, so the CSV can be fetched with the
     * Authorization header like every other read and handed to the browser as a
     * blob. A button that silently does nothing is the failure mode that note
     * was trying to avoid, and shipping it was how we got one.
     *
     * ⚠️ IT THROWS RATHER THAN SWALLOWING. D7 owns the button and therefore owns
     * the sentence beside it; a `catch` here would put the failure back where
     * nobody can see it.
     */
    const csv = await coverageCsv(classId, auth ?? {});
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "ncf-coverage.csv";
    anchor.click();
    // Released immediately: an object URL held after the click keeps the whole
    // class's coverage alive in the tab's memory until navigation.
    URL.revokeObjectURL(url);
  }, [classId, auth]);

  const codeResource = useResource<{ classId: string; code: string }>(
    (signal) => getClassPlayCode(classId, { ...auth, signal }),
    [classId],
  );

  /*
   * 🔴 A CONTEXT BAR, AND THE CLASS NAME IS A CHIP RATHER THAN LOOSE TEXT.
   *
   * This rendered `‹ Back`, then the class name as 13px muted text, then
   * `Change class` — three controls-shaped things where only two were controls,
   * and the class name (the single most important piece of context on every
   * screen in this tier) was the quietest thing on the row. It also duplicated
   * D1, which greeted the teacher with `· Class: 2B` immediately underneath.
   *
   * ⚠️ IT IS A RULE ACROSS THE TOP, NOT A FLOATING ROW. A teacher moving between
   * D4 → D5 → D6 needs one fixed place that says where she is; a bar that
   * changed height or alignment per screen is the thing that makes a tier feel
   * assembled rather than designed.
   */
  const chrome = (
    /*
     * ⚠️ `gap-y-3`, AND WITHOUT IT THE WRAP READS AS ONE PARAGRAPH. `gap-4` is
     * the gap between the greeting and the teacher chip; when the chip wraps
     * below on a phone that 16px becomes the vertical rhythm, and the chip lands
     * close enough to "Here's what's happening…" to look like part of it.
     */
    <header className="mb-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 sm:mb-8">
      <div className="min-w-0">
        <h1 className="font-dash text-dash-lg font-extrabold text-ink flex items-center gap-2 sm:text-2xl">
          {t.greeting(teacherName)}
        </h1>
        <p className="mt-1 font-dash text-dash-sm text-muted">
          {t.greetingSub(className)}
        </p>
      </div>
      {/* 🔴 IN THE HEADER, ABOVE EVERY SCREEN THIS TIER DRAWS. A closure is a
          fact about today that changes what a teacher does with the next hour,
          and putting it on one screen means it reaches whoever happened to be on
          that screen. See `StaffNotices`. */}
      <div className="flex items-center gap-3 rounded-full border border-line bg-surface px-4 py-2 shadow-sm">
        <Avatar name={teacherName || t.teacherFallback} size={36} />
        <div className="text-left">
          <div className="flex items-center gap-1 font-dash text-dash-sm font-extrabold text-ink">
            {teacherName || t.teacherFallback}{" "}
            <ChevronIcon size={14} className="text-muted" />
          </div>
          <div className="font-dash text-dash-xs font-bold text-muted flex items-center gap-1.5">
            <span>{className}</span>
            {codeResource.data?.code && (
              <span className="inline-flex items-center gap-1 rounded-md border border-pill-warning-line bg-pill-warning-bg px-1.5 py-0.5 text-[11px] font-extrabold text-pill-warning-fg shadow-2xs">
                <PrivacyIcon size={11} />
                <span>{groupCode(codeResource.data.code)}</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </header>
  );

  const screen = flow.screen.name;

  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => { setMenuOpen(false); }, []);
  /* Widening past `lg` brings the sidebar back — see `DashboardShell`'s note on
     why a class cannot do this and an effect has to. */
  const wide = useMediaQuery(BP.lg);
  useEffect(() => {
    if (wide) setMenuOpen(false);
  }, [wide]);

  /*
   * 🔴 THIS TIER OWNS ITS OWN CHROME, AND THAT IS WHY IT MISSED THE FIX
   * `DashboardShell` GOT. The docblock above says so outright — *"it is not
   * `DashboardShell`"* — so the drawer that shell grew for the school and
   * operator consoles never reached a teacher. Their sidebar was
   * `sticky h-dvh w-[15.5rem] shrink-0` with no query on it at all: 248px of a
   * 360px phone, leaving 112px for an attendance register of thirty children.
   *
   * ⚠️ THE PATTERN IS COPIED DELIBERATELY RATHER THAN SHARED. Merging this into
   * `DashboardShell` would mean giving that shell a fifth nav variant for one
   * caller whose sidebar carries a class switcher, a play-code chip and a
   * meadow — none of which the other four have. What IS shared is the piece
   * worth sharing: `Drawer` owns the Escape key, the focus trap and the scroll
   * lock, so there is exactly one implementation of those in the product.
   */
  const sidebarBody = (inDrawer: boolean) => (
    <>
      {/* §10 — THE WORDMARK IS ARTWORK, NEVER RE-TYPESET. */}
      <div className="mb-6 px-4">
        <img
          src="/kalqy-wordmark.png"
          alt="Kalqy"
          width={2000}
          height={585}
          className="h-7 w-auto"
        />
        <span className="mt-2 block font-dash text-dash-xs font-bold tracking-[0.1em] text-muted uppercase">
          {t.navClasses}
        </span>
      </div>

      <NavItem
        icon={<ReportsIcon size={18} strokeWidth={2.4} />}
        active={screen === "D4"}
        onClick={() => dispatch({ type: "CHOOSE_DASHBOARD" })}
      >
        {t.overview}
      </NavItem>
      <NavItem
        icon={<ChildrenIcon size={18} strokeWidth={2.4} />}
        active={screen === "STUDENTS" || screen === "D5"}
        onClick={() => dispatch({ type: "OPEN_STUDENTS" })}
      >
        {t.navStudents}
      </NavItem>
      <NavItem
        icon={<CheckIcon size={18} strokeWidth={2.4} />}
        active={screen === "ATTENDANCE"}
        onClick={() => dispatch({ type: "OPEN_ATTENDANCE" })}
      >
        {t.navAttendance}
      </NavItem>
      {/*
        🔴 D9 — THE FAMILY, WHICH THIS BAR HAD NO ROW FOR. Everything above it is
        about the class as a set; this is the one place a teacher speaks to one
        home. It sits above Play mode because it is a reading-and-replying task
        like the three above it, and Play mode is the fork into the lesson.
      */}
      <NavItem
        icon={<VoiceIcon size={18} strokeWidth={2.4} />}
        active={screen === "MESSAGES"}
        onClick={() => dispatch({ type: "OPEN_MESSAGES" })}
      >
        {t.navMessages}
      </NavItem>
      {/*
        🔴 PLAY OPENS THE SETUP SCREEN NOW, NOT THE PICK-A-CHILD LIST. D2 → D3
        ends at a launch button that is deliberately disabled, so this door led a
        teacher two screens to be told no. What it opens instead is the class
        code and the register the children sign in against — and D2 is still one
        tap away from there, for the day Play Mode is wired.
      */}
      <NavItem
        icon={<PlayIcon size={18} strokeWidth={2.4} />}
        active={screen === "PLAY" || screen === "D2" || screen === "D3"}
        onClick={() => dispatch({ type: "OPEN_PLAY_SETUP" })}
      >
        {t.navPlay}
      </NavItem>
      <NavItem
        icon={<TrendUpIcon size={18} strokeWidth={2.4} />}
        active={screen === "D6"}
        onClick={() => dispatch({ type: "OPEN_LEADERBOARD" })}
      >
        {t.navLeaderboard}
      </NavItem>
      <NavItem
        icon={<ReportsIcon size={18} strokeWidth={2.4} />}
        active={screen === "D7"}
        onClick={() => dispatch({ type: "OPEN_REPORTS" })}
      >
        {t.navReports}
      </NavItem>

      <div className="mt-auto flex flex-col gap-1 pt-6 z-10">
        {canChangeClass && (
          <NavItem
            icon={<SchoolIcon size={18} strokeWidth={2.4} />}
            onClick={() => onPickClass(null)}
          >
            {t.changeClass}
          </NavItem>
        )}
        {onSignOut && (
          <NavItem
            icon={<PrivacyIcon size={18} strokeWidth={2.4} />}
            onClick={onSignOut}
          >
            {t.signOut}
          </NavItem>
        )}
      </div>

      {/*
        Garden Illustration at Sidebar Bottom.

        ⚠️ NOT DRAWN IN THE DRAWER. Its negative margins (`-mx-4 -mb-6`) are
        measured against the `<aside>`'s own padding; inside the drawer's panel
        they bleed past the rounded edge. It is decoration filling a column's
        dead space, and a drawer opened for one tap has no dead space to fill.
      */}
      <div className={`mt-2 -mx-4 -mb-6 h-16 overflow-hidden rounded-b-xl pointer-events-none select-none shrink-0 ${inDrawer ? "hidden" : ""}`}>
        <svg
          className="w-full h-full"
          viewBox="0 0 240 64"
          preserveAspectRatio="none"
        >
          <path
            d="M0,40 C60,25 120,45 180,30 C210,22 230,35 240,28 L240,64 L0,64 Z"
            fill="#bbf7d0"
            opacity="0.8"
          />
          <path
            d="M0,48 C50,35 110,50 170,38 C200,32 225,42 240,36 L240,64 L0,64 Z"
            fill="#4ade80"
          />
          {/* Flower Left */}
          <g transform="translate(30, 44)">
            <circle cx="0" cy="-4" r="3" fill="#f43f5e" />
            <circle cx="4" cy="0" r="3" fill="#f43f5e" />
            <circle cx="0" cy="4" r="3" fill="#f43f5e" />
            <circle cx="-4" cy="0" r="3" fill="#f43f5e" />
            <circle cx="0" cy="0" r="2.5" fill="#facc15" />
          </g>
          {/* Flower Center */}
          <g transform="translate(85, 48)">
            <circle cx="0" cy="-3" r="2.5" fill="#facc15" />
            <circle cx="3" cy="0" r="2.5" fill="#facc15" />
            <circle cx="0" cy="3" r="2.5" fill="#facc15" />
            <circle cx="-3" cy="0" r="2.5" fill="#facc15" />
            <circle cx="0" cy="0" r="2" fill="#fb923c" />
          </g>
          {/* Flower Right */}
          <g transform="translate(190, 44)">
            <circle cx="0" cy="-4" r="3" fill="#f43f5e" />
            <circle cx="4" cy="0" r="3" fill="#f43f5e" />
            <circle cx="0" cy="4" r="3" fill="#f43f5e" />
            <circle cx="-4" cy="0" r="3" fill="#f43f5e" />
            <circle cx="0" cy="0" r="2.5" fill="#facc15" />
          </g>
        </svg>
      </div>
    </>
  );

  return (
    /* `safe-px` on the root — the one box here without its own `px-*`. */
    <div className="safe-px flex min-h-dvh">
      <aside className="sticky top-0 z-20 hidden h-dvh w-[15.5rem] shrink-0 flex-col gap-1 overflow-y-auto border-r border-line bg-surface px-4 py-6 lg:flex">
        {sidebarBody(false)}
      </aside>

      <Drawer open={menuOpen} onClose={closeMenu} label="Class navigation">
        <div
          className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-4 pt-6 safe-pb-6"
          /* Same delegated close as the two shells — every row in this column
             dispatches a flow action, and none of them can reach the drawer. */
          onClick={closeMenu}
        >
          {sidebarBody(true)}
        </div>
      </Drawer>

      {/* ⚠️ 24px, not 28 — 28 is off the spacing scale, and this tier draws its
          own chrome so it has to match `DashboardShell`'s main pane by hand.
          The ramp below it is the phone's: 32px either side of a 360px screen
          is 18% of it spent on margin. */}
      <main className="min-w-0 flex-1 px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6">
        {/*
          🔴 THE PHONE'S ONLY DOOR INTO THE NAV. `lg:hidden` because above `lg`
          the sidebar beside it draws the wordmark and every item this opens.
          Sticky, because an attendance register is thirty rows long and a
          teacher two-thirds down it must not have to scroll back to switch
          screens.
        */}
        <div className="sticky top-0 z-30 -mx-4 mb-4 flex items-center gap-2 border-b border-line bg-canvas/95 px-4 py-2 backdrop-blur-sm sm:-mx-6 sm:px-6 lg:hidden">
          <button
            type="button"
            onClick={() => { setMenuOpen(true); }}
            aria-label="Open class navigation"
            aria-expanded={menuOpen}
            aria-haspopup="dialog"
            className="tap-target -ml-1 flex h-11 w-11 items-center justify-center rounded-lg text-ink transition-colors hover:bg-primary-tint"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          {/* §10 — THE WORDMARK IS ARTWORK, NEVER RE-TYPESET. */}
          <img src="/kalqy-wordmark.png" alt="Kalqy" width={2000} height={585} className="h-6 w-auto" />
          <span className="min-w-0 truncate border-l border-line-strong pl-2 font-dash text-dash-xs font-bold tracking-[0.1em] text-muted uppercase">
            {className || t.navClasses}
          </span>
        </div>

        {chrome}

        <StaffNotices token={auth?.token} />

        {flow.screen.name === "ATTENDANCE" && (
          <AttendanceScreen
            classId={flow.screen.classId}
            lang={lang}
            auth={auth}
            onSaved={onDataChanged}
            canTakeRegister={canTakeRegister}
            registerHeldBy={registerHeldBy}
          />
        )}

        {flow.screen.name === "MESSAGES" && (
          <>
            <MessagesScreen
              classId={flow.screen.classId}
              lang={lang}
              auth={auth}
            />
            {/*
              🔴 THE NOTICE BOARD IS UNDER THE THREADS, NOT BESIDE THEM. Both
              reach the same families and the difference is the audience: one
              message to one home, or one notice to all thirty. Putting the
              broadcast alongside a conversation is how somebody sends the whole
              class an answer meant for one parent.
            */}
            <div className="mt-6">
              <ClassNotices
                classId={flow.screen.classId}
                lang={lang}
                auth={auth}
                teacherName={teacherName}
              />
            </div>
          </>
        )}

        {flow.screen.name === "PLAY" && (
          <PlaySetupScreen
            classId={flow.screen.classId}
            lang={lang}
            auth={auth}
            onPlayAsChild={() => {
              dispatch({ type: "CHOOSE_PLAY" });
            }}
          />
        )}

        {flow.screen.name === "D2" && (
          <RosterScreen
            classId={flow.screen.classId}
            lang={lang}
            auth={auth}
            onPick={(playerId) => {
              /* 🔴 Straight to D3. The machine has no D2 → game edge at all. */
              dispatch({ type: "PICK_CHILD", playerId });
            }}
          />
        )}

        {flow.screen.name === "D3" && (
          <>
            <LaunchConfirm
              classId={flow.screen.classId}
              playerId={flow.screen.playerId}
              /*
               * ⚠️ THE LABEL IS THE GAME'S `id`, AND THAT IS THE EXISTING
               * CONVENTION RATHER THAN A SHORTCUT. `GameContent` carries no human
               * title — OD-7 leaves the final game names open — and the child's
               * own `LibraryScreen` already renders `title={game.id}` for exactly
               * that reason. Inventing display names here would create a second
               * vocabulary for the five games, in the tier a school buyer reads.
               */
              games={GAMES.map((g) => ({ id: g.id, title: g.id }))}
              lang={lang}
              auth={auth}
              onBack={() => {
                dispatch({ type: "BACK" });
              }}
              /*
               * ✅ LAUNCH DOES SOMETHING NOW, AND THE OLD REFUSAL WAS WRONG ABOUT
               * WHY IT COULD NOT.
               *
               * This button carried a paragraph explaining that Teacher Play Mode
               * *"means changing the gameplay write path, which `offline-sync.md`
               * governs and which is the least forgiving code in the product"*.
               * That was right about the danger and wrong about the fix: nothing
               * in the write path changes. `KidApp` plays as whoever
               * `kidSession.ts` names, `device.ts` already prefers a signed-in
               * child's token over the tablet's **at flush time**, and every
               * event still leaves as `context: 'device'` under that child's own
               * credential. What was missing was a second way to MINT the session
               * `POST /auth/kid` mints — which is `POST
               * /players/:playerId/play-session`, scoped and audited server-side.
               *
               * 🔴 SO THE ATTRIBUTION CONCERN IS ANSWERED RATHER THAN ACCEPTED.
               * The fear was *"attributing a real child's session to a device
               * account"*; the session minted here names the child, so progress
               * saves to roll 03 because the token says roll 03.
               *
               * ⚠️ `LAUNCH` IS STILL DISPATCHED, BEFORE THE NAVIGATION. D3 is the
               * only screen the machine accepts it from, which is what makes
               * `playingAs` a structural guarantee rather than a habit — and it is
               * the state the failure notice below reads.
               */
              onLaunch={(gameId) => {
                dispatch({ type: "LAUNCH" });
                void launch(
                  flow.screen.name === "D3" ? flow.screen.playerId : "",
                  gameId,
                );
              }}
            />
            {/*
              ⚠️ THE ONLY THING LEFT TO SAY HERE IS A FAILURE. A launch that
              worked leaves this screen entirely — the tablet is in the child's
              hands — so a pill describing the happy path would be a message
              nobody is present to read.
            */}
            {launchError !== null && (
              <p role="alert" className="mt-4 max-w-prose">
                <StatusPill tone="danger" icon={<AlertIcon size={14} />}>
                  {launchError}
                </StatusPill>
              </p>
            )}
          </>
        )}

        {(flow.screen.name === "D1" || flow.screen.name === "D4") && (
          <ClassOverviewScreen
            classId={flow.screen.name === "D1" ? classId : flow.screen.classId}
            lang={lang}
            auth={auth}
            onOpenChild={(playerId, name) => {
              setOpenChild({ id: playerId, name });
              dispatch({ type: "OPEN_CHILD", playerId });
            }}
            /*
             * ⚠️ THE SAME ACTION THE SIDEBAR'S "Students" ITEM DISPATCHES, and
             * deliberately not a new one. `OPEN_STUDENTS` already drops the
             * `classId` on purpose — the note on the `STUDENTS` screen calls
             * that *"the point"*, since the list is every child across every
             * class this teacher takes. A second action that carried the open
             * class would make the same door lead somewhere else depending on
             * which handle you pulled.
             */
            onOpenStudents={() => dispatch({ type: "OPEN_STUDENTS" })}
          />
        )}

        {/*
        ✅ D5 IS `StudentPage` NOW, NOT `StudentDetail`.
        `StudentDetail` showed an AI summary and a list of events. This screen
        adds the identity, the linked parent, the progress snapshot, the
        per-skill breakdown as LEVELS, the sittings — and the four
        `@Roles('TEACHER','ADMIN')` controls that had no UI at all: T1 reassign,
        T4 band, T7 absent, and the class move.
      */}
        {flow.screen.name === "STUDENTS" && (
          <StudentsScreen
            lang={lang}
            auth={auth}
            classId={classId}
            onOpenChild={(playerId, childClassId, name) => {
              setOpenChild({ id: playerId, name });
              /* 🔴 The CHILD's class, not the open one — they may be in any
               section this teacher teaches. See `teacherFlow`. */
              dispatch({ type: "OPEN_CHILD", playerId, classId: childClassId });
            }}
          />
        )}

        {flow.screen.name === "D5" && (
          <StudentPage
            playerId={flow.screen.playerId}
            classId={flow.screen.classId}
            classes={classes}
            lang={lang}
            auth={auth}
            onBack={() => {
              setOpenChild(null);
              dispatch({ type: "BACK" });
            }}
            onChanged={onDataChanged}
          />
        )}

        {flow.screen.name === "D6" && (
          <LeaderboardScreen
            classId={flow.screen.classId}
            lang={lang}
            auth={auth}
          />
        )}

        {flow.screen.name === "D7" && (
          <ReportsScreen
            classId={flow.screen.classId}
            lang={lang}
            auth={auth}
            onExportCsv={exportCoverage}
          />
        )}

        {/*
          ✅ THE "NOTHING CAN ACT ON THIS" NOTICE IS GONE, AND ITS ABSENCE IS THE
          POINT. It read *"a launch that the machine accepted but nothing can act
          on must not look like it worked"* — honest while the launch went
          nowhere, and false now: `playingAs` is set and the tablet is in the
          child's hands. The failure case is drawn on D3 itself, beside the
          button that caused it.
        */}
      </main>
    </div>
  );
}

/**
 * What the school has just told this tier.
 *
 * 🔴 IT IS A NOTICE, NOT AN INBOX. The frames it draws arrived while this tab
 * was open and are gone on reload — `useStaffEvents` records why: persisting
 * them would imply a durable record of what a teacher has and has not seen, and
 * nothing on the server backs that claim. What IS durable is the thing each one
 * points at; a closure is in the calendar whether or not this panel ever drew
 * it.
 *
 * 🔴 SO IT IS ADDITIVE AND NEVER LOAD-BEARING. Nothing on this tier waits for
 * it, nothing breaks without it, and a teacher whose laptop was shut when the
 * frame went out is not worse off than before this existed — they read the
 * holiday off the screen, as they always did. That is what makes a best-effort
 * transport an acceptable thing to put on a dashboard.
 *
 * ⚠️ NOTHING IS DRAWN WHEN THERE IS NOTHING TO SAY. An empty "no notices" card is
 * a permanent piece of furniture teaching a teacher to skip the region it sits
 * in — the same argument that removed the always-zero Archived classes tile from
 * the school dashboard.
 */
function StaffNotices({ token }: { token?: string }) {
  const events = useStaffEvents(token);
  if (events.length === 0) return null;

  return (
    <div className="mb-6 flex flex-col gap-2" aria-live="polite">
      {events.map((event) => (
        <div
          key={`${event.type}-${event.at}`}
          className="flex items-start gap-3 rounded-xl border border-line bg-surface px-4 py-3 shadow-card"
        >
          <span className="mt-0.5 shrink-0 text-primary">
            <InfoIcon size={18} />
          </span>
          <div className="min-w-0">
            <p className="font-dash text-dash-sm font-extrabold text-ink">
              {event.title}
            </p>
            {event.detail !== undefined && (
              <p className="mt-0.5 font-dash text-dash-xs text-muted">
                {event.detail}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
